<template>
  <div id="stops-page">
    <div class="header-block d-flex align-items-center justify-content-between">
      <div class="d-flex align-items-center">
        <div class="page-title">Paradas</div>
        <div class="date-range-info" v-if="dateRangeText && !hasError">{{ dateRangeText }}</div>
        <div class="error-info" v-if="hasError">
          <feather-icon icon="AlertCircleIcon" size="16" class="mr-25" />
          Erro ao buscar os dados
        </div>
      </div>
      <b-button class="btn-filters" variant="outline-primary" size="sm" @click="filtersOpen = true">
        <feather-icon icon="FilterIcon" class="mr-50" />
        Filtros
      </b-button>
    </div>

    <div class="body-block">
      <!-- Gráfico de Pareto - Eventos de Paradas -->
      <b-row class="chart-grid">
        <b-col cols="12" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos de Paradas</div>
            <div class="chart-description">Análise dos principais motivos de paradas com tempo parado e percentual
              acumulado seguindo o princípio de Pareto</div>
            <app-echart-bar class="chart-body" :optionData="stopsEvents" :height="eventsChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
      </b-row>

      <!-- Gráfico de Gantt - Paradas por Recurso -->
      <b-row class="chart-grid">
        <b-col cols="12" class="mb-1">
          <b-card class="chart-card gantt-chart-card h-100">
            <div class="chart-header">Gráfico de Gantt</div>
            <div class="chart-description">Visualização temporal das paradas por recurso, mostrando a duração e o
              período em que cada evento ocorreu</div>
            <app-echart-bar ref="ganttChartComponent" class="chart-body" :optionData="ganttChart"
              :height="ganttChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
      </b-row>

      <b-row class="chart-grid">
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Área</div>
            <app-echart-bar class="chart-body" :optionData="stopsByArea" :height="smallChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Turno</div>
            <app-echart-bar class="chart-body" :optionData="stopsByShift" :height="smallChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Mês</div>
            <app-echart-bar class="chart-body" :optionData="stopsByMonth" :height="smallChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Dia</div>
            <app-echart-bar class="chart-body" :optionData="stopsByDay" :height="smallChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Recurso</div>
            <app-echart-bar class="chart-body" :optionData="stopsByResource" :height="smallChartHeight"
              :key="stopsKey" />
          </b-card>
        </b-col>
        <b-col cols="12" sm="6" lg="4" class="mb-1">
          <b-card class="chart-card h-100">
            <div class="chart-header">Eventos por Tipo</div>
            <app-echart-bar class="chart-body" :optionData="stopsByType" :height="smallChartHeight" :key="stopsKey" />
          </b-card>
        </b-col>
      </b-row>

      <b-row class="chart-grid">
        <b-col cols="12" class="mb-1">
          <b-card class="table-card h-100">
            <div class="chart-header d-flex justify-content-between align-items-center">
              <span>Tabela de Eventos de Paradas</span>
              <b-button class="btn-filters" variant="outline-primary" size="sm" @click="exportStopsCsv">
                Exportar CSV
              </b-button>
            </div>
            <div class="table-wrapper">
              <b-table small striped hover :fields="tableFields" :items="tableItems" :per-page="tablePageSize"
                :current-page="currentPage" head-variant="light" class="stops-table" responsive :key="stopsKey" />
            </div>
            <div class="d-flex justify-content-end mt-50">
              <b-pagination v-model="currentPage" :total-rows="tableItems.length" :per-page="tablePageSize" size="sm"
                align="right" />
            </div>
          </b-card>
        </b-col>
      </b-row>


    </div>

    <!-- Right sidebar filters -->
    <b-sidebar right shadow bg-variant="white" width="320px" v-model="filtersOpen" backdrop>
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 px-1 py-1">
          <span class="font-weight-bold">Filtros</span>
          <span style="cursor:pointer" @click="filtersOpen = false">
            <feather-icon icon="XIcon" size="18" />
          </span>
        </div>
      </template>
      <div class="filters-card">
        <div class="filter">
          <label class="filter-label">Filtro Rápido:</label>
          <div class="quick-filter-buttons">
            <button v-for="days in quickFilterDays" :key="days"
              :class="['quick-filter-btn', { active: filterDays === days }]" @click="setQuickFilter(days)">
              {{ days }} dias
            </button>
          </div>
        </div>
        <div class="filter">
          <label class="filter-label">De:</label>
          <input type="date" v-model="startDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter">
          <label class="filter-label">Até:</label>
          <input type="date" v-model="endDate" class="date-input-sidebar" @change="onManualDateChange" />
        </div>
        <div class="filter" v-for="f in visibleFilters" :key="f.key">
          <label class="filter-label">{{ f.label }}</label>
          <b-form-select v-model="f.value" :options="f.options" size="sm" />
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end px-1 pb-1">
          <b-button size="sm" class="mr-50" variant="outline-secondary" @click="clearFilters"> Limpar</b-button>
          <b-button size="sm" variant="primary" @click="applyFilters">Aplicar</b-button>
        </div>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import { BRow, BCol, BCard, BFormSelect, BButton, BTable, BSidebar, BPagination } from 'bootstrap-vue';
import { mapMutations } from 'vuex';
import AppEchartBar from '@core/components/charts/echart/AppEchartBar.vue';
import { useFilters } from '@/views/hooks/useFilters';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import axios from 'axios';
import { loadResources } from '@/views/utils/getResources';

const GANTT_BAR_COLOR = '#f3811a';
const DAY_IN_MS = 24 * 60 * 60 * 1000;
const HOUR_IN_MS = 60 * 60 * 1000;
const HALF_HOUR_IN_MS = 30 * 60 * 1000;
const QUARTER_HOUR_IN_MS = 15 * 60 * 1000;
const TWELVE_HOURS_IN_MS = 12 * HOUR_IN_MS;

const padNumber = (value) => value.toString().padStart(2, '0');

const formatAxisDateLabel = (value) => {
  const numericValue = value instanceof Date ? value.getTime() : Number(value);
  const date = Number.isNaN(numericValue) ? new Date(value) : new Date(numericValue);
  if (Number.isNaN(date.getTime())) return '';
  return `${padNumber(date.getDate())}/${padNumber(date.getMonth() + 1)}`;
};

const formatAxisHourLabel = (value, intervalMs) => {
  const numericValue = value instanceof Date ? value.getTime() : Number(value);
  const date = Number.isNaN(numericValue) ? new Date(value) : new Date(numericValue);
  if (Number.isNaN(date.getTime())) return '';
  const hours = padNumber(date.getHours());
  const minutes = padNumber(date.getMinutes());

  if (intervalMs >= HOUR_IN_MS) {
    return `${hours}h`;
  }

  if (intervalMs === HALF_HOUR_IN_MS) {
    return minutes === '30' ? `${hours}h30` : `${hours}h`;
  }

  if (intervalMs === QUARTER_HOUR_IN_MS) {
    if (minutes === '00') return `${hours}h`;
    return `${hours}h${minutes}`;
  }

  return `${hours}:${minutes}`;
};

const buildTimeAxisConfig = ({ axisMin = null, axisMax = null, mode = 'day', interval = DAY_IN_MS } = {}) => {
  const showHours = mode === 'hour';
  return {
    type: 'time',
    min: axisMin,
    max: axisMax,
    boundaryGap: [0, 0],
    axisLine: { show: false },
    axisTick: {
      show: true,
      inside: false,
      length: showHours ? 4 : 6,
      alignWithLabel: true,
      lineStyle: { color: '#d8d6de' }
    },
    minorTick: { show: false },
    splitLine: { show: true, lineStyle: { color: '#f0f0f0' } },
    axisLabel: showHours
      ? {
        formatter: (val) => formatAxisHourLabel(val, interval),
        fontSize: 11,
        margin: 18,
        hideOverlap: true
      }
      : {
        formatter: (val) => formatAxisDateLabel(val),
        fontSize: 11,
        margin: 12
      },
    interval,
    minInterval: interval
  };
};

const createBaseGanttOption = () => ({
  grid: { left: 80, right: 32, top: 42, bottom: 80 },
  xAxis: buildTimeAxisConfig({ mode: 'day', interval: DAY_IN_MS }),
  yAxis: {
    type: 'category',
    data: [],
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      fontSize: 12,
      width: 80,
      overflow: 'break',
      lineHeight: 16,
      formatter: function (value) {
        if (!value) return '';
        const maxLength = 8; // Máximo de caracteres por linha
        if (value.length <= maxLength) return value;

        const words = value.split(' ');
        const lines = [];
        let currentLine = '';

        for (let i = 0; i < words.length; i++) {
          const word = words[i];
          const testLine = currentLine ? currentLine + ' ' + word : word;

          if (testLine.length <= maxLength) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }

        if (currentLine) lines.push(currentLine);
        return lines.join('\n');
      }
    },
    splitArea: {
      show: true,
      areaStyle: {
        color: ['#ffffff', '#f0f2f5']
      }
    }
  },
  legend: {
    bottom: 34,
    left: 'center',
    icon: 'roundRect',
    itemWidth: 12,
    itemHeight: 8,
    textStyle: { fontSize: 11 },
    selectedMode: false,
    data: [],
    show: false
  },
  tooltip: {
    trigger: 'item'
  },
  dataZoom: [
    {
      type: 'slider',
      show: false,
      bottom: 8,
      height: 18,
      borderColor: '#f0f0f0',
      backgroundColor: '#ffffff',
      fillerColor: 'rgba(90, 141, 238, 0.2)',
      handleSize: '80%',
      labelFormatter: (val) => formatAxisDateLabel(val),
      filterMode: 'none'
    },
    {
      type: 'inside',
      filterMode: 'none',
      startValue: null,
      endValue: null
    }
  ],
  series: []
});

export default {
  name: 'StopsPage',
  components: { BRow, BCol, BCard, BFormSelect, BButton, BTable, BSidebar, BPagination, AppEchartBar, FeatherIcon, SysButton: () => import('@core/components/sys-button/index.vue') },
  data() {
    const filtersHelper = useFilters();

    // Função para quebra de linha automática nos labels
    const labelFormatter = function (value) {
      if (!value) return '';
      const maxWidth = 12;
      const words = value.split(' ');
      const lines = [];
      let currentLine = '';

      for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const testLine = currentLine ? currentLine + ' ' + word : word;

        if (testLine.length <= maxWidth) {
          currentLine = testLine;
        } else {
          if (currentLine) lines.push(currentLine);
          currentLine = word;
        }
      }

      if (currentLine) lines.push(currentLine);
      return lines.join('\n');
    };

    return {
      filtersOpen: false,
      // Table pagination
      currentPage: 1,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1440,
      dateRangeText: '',
      hasError: false,
      startDate: '',
      endDate: '',
      filterDays: 7,
      quickFilterDays: [7, 30, 60, 90],
      filters: filtersHelper.createFilters(),
      filtersHelper,
      resourceMap: {}, // Mapeamento de nome do recurso para ID
      areaMap: {}, // mapeia 
      stopsKey: 0, // contador para forcar o re-render dos graficos
      colorPalette: [
        '#f3811a',
        '#28c76f',
        '#ea5455',
        '#00cfe8',
        '#7367f0',
        '#ff9f43',
        '#1e90ff',
        '#9c27b0',
        '#2ecc71',
        '#34495e'
      ],
      areaColors: {},
      shiftColors: {},
      monthColors: {},
      dayColors: {},
      resourceColors: {},
      typeColors: {},

      ganttChart: createBaseGanttOption(),
      ganttAxisMode: { mode: 'day', interval: DAY_IN_MS },
      ganttAxisLimits: { min: null, max: null },
      ganttCurrentRange: { start: null, end: null },
      ganttZoomHandler: null,

      // Charts options (8 cards)
      stopsEvents: {
        grid: { left: 80, right: 80, bottom: 80, top: 60 },
        legend: {
          top: 5,
          right: 10,
          orient: 'horizontal',
          itemWidth: 14,
          itemHeight: 10,
          textStyle: {
            fontSize: 11
          }
        },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: [
          {
            type: 'value',
            name: 'Tempo parado (min)',
            nameLocation: 'middle',
            nameGap: 45,
            nameTextStyle: {
              fontSize: 11,
              color: '#6c757d'
            },
            axisLabel: {
              fontSize: 10,
              color: '#6c757d'
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#e0e0e0'
              }
            }
          },
          {
            type: 'value',
            name: '% acumulado',
            nameLocation: 'middle',
            nameGap: 45,
            nameTextStyle: {
              fontSize: 11,
              color: '#6c757d'
            },
            min: 0,
            max: 100,
            axisLabel: {
              formatter: '{value}%',
              fontSize: 10,
              color: '#6c757d'
            },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: 'Tempo parado',
            type: 'bar',
            data: [],
            barMaxWidth: 50
          },
          {
            name: '% acumulado',
            type: 'line',
            yAxisIndex: 1,
            data: [],
            smooth: false,
            symbol: 'circle',
            symbolSize: 8,
            lineStyle: {
              color: '#FF4560',
              width: 2
            },
            itemStyle: {
              color: '#FF4560'
            }
          }
        ]
      },
      stopsByArea: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          },
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },
      stopsByShift: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },
      stopsByMonth: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },
      stopsByDay: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },
      stopsByResource: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: { type: 'value', splitLine: { show: false } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },
      stopsByType: {
        grid: { left: 50, right: 20, bottom: 60, top: 40 },
        xAxis: {
          type: 'category',
          data: [],
          axisLabel: {
            rotate: 45,
            fontSize: 10,
            interval: 0,
            formatter: labelFormatter
          }
        },
        yAxis: { type: 'value', splitLine: { show: true } },
        series: [{ name: 'Tempo', type: 'bar', data: [], barMaxWidth: 40 }]
      },

      tableFields: [
        { key: 'ordem', label: 'Ordem', sortable: true },
        { key: 'recurso', label: 'Recurso', sortable: true },
        { key: 'evento', label: 'Evento', sortable: true },
        { key: 'tempo', label: 'Tempo', sortable: true },
        { key: 'inicio', label: 'InÃ­cio da parada' },
        { key: 'fim', label: 'Fim da parada' }
      ],
      tableItems: []
    };
  },
  mounted() {
    this.initializeDates();
    this.loadStoredColors();
    this.loadStopsData();
    if (typeof window !== 'undefined') {
      this._resizeHandler = () => (this.windowWidth = window.innerWidth);
      window.addEventListener('resize', this._resizeHandler);
    }
  },
  beforeDestroy() {
    this.detachGanttZoomHandler();
    if (this._resizeHandler) window.removeEventListener('resize', this._resizeHandler);
  },
  computed: {
    visibleFilters() {
      return this.filters.filter(f => !['year', 'month', 'day'].includes(f.key));
    },
    activeFilters() {
      return {
        year: this.filtersHelper.getFilterValue(this.filters, 'year'),
        month: this.filtersHelper.getFilterValue(this.filters, 'month'),
        day: this.filtersHelper.getFilterValue(this.filters, 'day'),
        shift: this.filtersHelper.getFilterValue(this.filters, 'shift'),
        area: this.filtersHelper.getFilterValue(this.filters, 'area'),
        resource: this.filtersHelper.getFilterValue(this.filters, 'resource')
      }
    },

    smallChartHeight() {
      const w = this.windowWidth;
      if (w >= 1600) return 360;
      if (w >= 1366) return 320;
      if (w >= 1200) return 300;
      if (w >= 992) return 280;
      if (w >= 768) return 260;
      return 240;
    },
    ganttChartHeight() {
      const rowCount = (this.ganttChart && this.ganttChart.yAxis && this.ganttChart.yAxis.data)
        ? this.ganttChart.yAxis.data.length
        : 6;
      const perRow = 34;
      const margins = 150;
      const base = this.smallChartHeight + 60;
      const calculated = margins + rowCount * perRow;
      return Math.max(base, Math.min(820, calculated));
    },
    // More height for long category list to increase spacing between lines
    eventsChartHeight() {
      const count = (this.stopsEvents && this.stopsEvents.yAxis && this.stopsEvents.yAxis.data)
        ? this.stopsEvents.yAxis.data.length
        : 16;
      const perItem = 24; // px per category for readability
      const margins = 110; // more breathing room for the grid
      const base = this.smallChartHeight + 40;
      const desired = margins + count * perItem;
      return Math.max(base, Math.min(680, desired));
    },

    tablePageSize() {
      const w = this.windowWidth;
      if (w >= 1600) return 8;
      if (w >= 1366) return 7;
      if (w >= 1200) return 6;
      if (w >= 992) return 5;
      return 4;
    },

  },
  methods: {
    ...mapMutations('app', ['UPDATE_FLAG_SHOW_OVERLAY']),

    loadStoredColors() {
      if (typeof window === 'undefined') return;
      this.areaColors = this.safeJSONParse(localStorage.getItem('stopsAreaColors')) || {};
      this.shiftColors = this.safeJSONParse(localStorage.getItem('stopsShiftColors')) || {};
      this.monthColors = this.safeJSONParse(localStorage.getItem('stopsMonthColors')) || {};
      this.dayColors = this.safeJSONParse(localStorage.getItem('stopsDayColors')) || {};
      this.resourceColors = this.safeJSONParse(localStorage.getItem('stopsResourceColors')) || {};
      this.typeColors = this.safeJSONParse(localStorage.getItem('stopsTypeColors')) || {};
    },

    saveColors(colorType, colors) {
      if (typeof window === 'undefined') return;
      const key = `stops${colorType}Colors`;
      localStorage.setItem(key, JSON.stringify(colors));
    },

    getColorForCategory(categoryName, colorMap, colorType) {
      const key = categoryName || 'default';
      if (colorMap[key]) {
        return colorMap[key];
      }
      const usedColors = new Set(Object.values(colorMap));
      const available = this.colorPalette.filter(color => !usedColors.has(color));
      const pool = available.length ? available : this.colorPalette;
      const color = pool[Math.floor(Math.random() * pool.length)];
      colorMap[key] = color;
      this.saveColors(colorType, colorMap);
      return color;
    },

    async loadStopsData() {
      this.UPDATE_FLAG_SHOW_OVERLAY(true);

      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn('Nenhum site selecionado para carregar dados de Paradas.');
        this.UPDATE_FLAG_SHOW_OVERLAY(false);
        return;
      }

      try {
        const params = { SiteId: siteId };
        this.loadWorkcenters();
        this.loadResourcesList();
        // Adicionar FilterDays se estiver definido
        if (this.filterDays) {
          params.FilterDays = this.filterDays;
        }

        // Adicionar datas se estiverem definidas
        if (this.startDate && this.endDate) {
          params.StartDate = this.startDate;
          params.EndDate = this.endDate;
        }

        // filtro de recurso
        const selectedResource = this.filtersHelper.getFilterValue(this.filters, 'resource');
        if (selectedResource && selectedResource !== 'Todos' && this.resourceMap[selectedResource]) {
          params.ResourcesIds = String(this.resourceMap[selectedResource]);
        }

        // filtro de area
        const selectedArea = this.filtersHelper.getFilterValue(this.filters, 'area');
        if (selectedArea && selectedArea !== 'Todos' && this.areaMap[selectedArea]) {
          params.WorkcentersIds = String(this.areaMap[selectedArea]);
        }

        // Atualizar o texto do range de datas
        this.updateDateRangeText();
        const response = await this.$http2.get("/api/stopevents/calculate", {
          params: params
        });
        // console.log('response', response);
        this.applyStopsResponse(response?.data?.data || {});
        await this.loadGanttDetails({ ...params });
        this.hasError = false;
      } catch (error) {
        console.error('Erro ao carregar dados de Paradas', error);
        this.hasError = true;
      } finally {
        this.UPDATE_FLAG_SHOW_OVERLAY(false);
      }
    },

    async loadGanttDetails(params = {}) {
      try {
        const response = await this.$http2.get("/api/stopevents/details", {
          params
        });
        const details = Array.isArray(response?.data?.data) ? response.data.data : [];
        this.updateGanttChart(details);
        this.updateTable(details);

      } catch (error) {
        console.error('Erro ao carregar detalhes de paradas para o Gantt', error);
        this.updateGanttChart([]);
      }
    },

    // TODO: AQUI QUANDO SUBIR PRA PROD VAI PUXAR DA FUNCAO GETWORKCENTERS
    async loadWorkcenters() {
      const siteId = this.filtersHelper.getCurrentSiteId(this.$cookies);

      if (!siteId) {
        console.warn("Nenhum site selecionado para carregar dados de OEE.");
        return;
      }

      const workcenters = await this.$http2.get("/api/myconfig/workcenters", {
        params: {
          siteId
        }
      });

      const areaNames = workcenters?.data?.data?.map(item => item?.workcenterName) || [];
      this.filtersHelper.updateFilterOptions(this.filters, 'area', areaNames, true);

      // setando o map de areas
      this.areaMap = Object.fromEntries(workcenters?.data?.data?.map(item => [item.workcenterName, item.workcenterId]) || []);
    },

    async loadResourcesList() {
      const resources = await loadResources(this.siteId, this.$http2);
      // console.log('resources', resources);
      const resourcesNames = resources?.map(item => item?.name) || [];
      // console.log('resourcesNames', resourcesNames);
      this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourcesNames, true);

      // setando o map de recursos
      this.resourceMap = Object.fromEntries(resources?.map(item => [item.name, item.id]) || []);
    },

    applyStopsResponse(payload = {}) {
      const {
        stopEventsByArea = [],
        stopEventsByShift = [],
        stopEventsByMonth = [],
        stopEventsByDay = [],
        stopEventsByResource = [],
        stopEventsByType = [],
        stopReasons = []
      } = payload || {};

      //adiciona os recursos no filtro
      // const resourceLabels = stopEventsByResource.map(item => item?.resourceName || 'Sem nome');
      // this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourceLabels, true);

      //adiciona os turnos no filtro
      const shiftLabels = stopEventsByShift.map(item => item?.shiftName || 'Sem nome');
      this.filtersHelper.updateFilterOptions(this.filters, 'shift', shiftLabels, true);

      // Evita re-filtrar no front; os dados já vieram do backend conforme os parâmetros

      // Atualizar gráficos
      this.updateAreaChart(stopEventsByArea);
      this.updateShiftChart(stopEventsByShift);
      this.updateMonthChart(stopEventsByMonth);
      this.updateDayChart(stopEventsByDay);
      this.updateResourceChart(stopEventsByResource);
      this.updateTypeChart(stopEventsByType);
      this.updateReasonsChart(stopReasons);
      // Atualizar tabela

      this.stopsKey++;
    },

    updateGanttChart(events = []) {
      if (!Array.isArray(events) || events.length === 0) {
        this.detachGanttZoomHandler();
        this.ganttAxisLimits = { min: null, max: null };
        this.ganttAxisMode = { mode: 'day', interval: DAY_IN_MS };
        this.ganttCurrentRange = { start: null, end: null };
        this.ganttChart = createBaseGanttOption();
        return;
      }

      const segments = this.splitEventsByDay(events);
      if (!segments.length) {
        this.detachGanttZoomHandler();
        this.ganttAxisLimits = { min: null, max: null };
        this.ganttAxisMode = { mode: 'day', interval: DAY_IN_MS };
        this.ganttCurrentRange = { start: null, end: null };
        this.ganttChart = createBaseGanttOption();
        return;
      }

      const resourceNames = Array.from(new Set(segments.map(item => item.resourceName)));
      resourceNames.sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));

      const minStart = Math.min(...segments.map(item => item.start.getTime()));
      const maxEnd = Math.max(...segments.map(item => item.end.getTime()));
      const axisMinDate = this.getStartOfDay(new Date(minStart));
      const axisMaxDate = new Date(this.getStartOfDay(new Date(maxEnd)).getTime() + DAY_IN_MS);
      const axisMin = axisMinDate.getTime();
      const axisMax = axisMaxDate.getTime();

      this.ganttAxisLimits = { min: axisMin, max: axisMax };

      const totalDays = Math.max(1, Math.ceil((axisMax - axisMin) / DAY_IN_MS));
      const visibleWindow = Math.min(7, totalDays);
      const visibleEnd = axisMin + visibleWindow * DAY_IN_MS;
      this.ganttCurrentRange = { start: axisMin, end: visibleEnd };

      const initialDisplay = this.resolveGanttAxisDisplay(visibleEnd - axisMin);
      this.ganttAxisMode = initialDisplay;

      const ganttOption = createBaseGanttOption();
      ganttOption.yAxis.data = resourceNames;
      ganttOption.xAxis = buildTimeAxisConfig({
        axisMin,
        axisMax,
        mode: initialDisplay.mode,
        interval: initialDisplay.interval
      });
      ganttOption.legend.show = false;
      ganttOption.legend.data = [];
      ganttOption.tooltip = {
        trigger: 'item',
        backgroundColor: '#ffffff',
        borderColor: '#d8d6de',
        borderWidth: 1,
        padding: 10,
        textStyle: { color: '#4e5154', fontSize: 12 },
        formatter: ({ data }) => {
          if (!data) return '';
          const startText = this.formatDateTime(data.start);
          const endText = this.formatDateTime(data.end);
          const durationText = this.formatDuration(data.durationMs);
          return [
            `<div><strong>${data.resourceName}</strong></div>`,
            `<div>${data.eventName}</div>`,
            `<div>Tipo: ${data.typeName}</div>`,
            `<div>Inicio: ${startText}</div>`,
            `<div>Fim: ${endText}</div>`,
            `<div>Duracao: ${durationText}</div>`
          ].join('');
        }
      };
      ganttOption.dataZoom[0] = {
        ...ganttOption.dataZoom[0],
        show: totalDays > visibleWindow,
        startValue: this.ganttCurrentRange.start,
        endValue: this.ganttCurrentRange.end
      };
      ganttOption.dataZoom[1] = {
        ...ganttOption.dataZoom[1],
        startValue: this.ganttCurrentRange.start,
        endValue: this.ganttCurrentRange.end
      };
      ganttOption.series = [{
        name: 'Paradas',
        type: 'custom',
        renderItem(params, api) {
          const categoryIndex = api.value(0);
          const coordSys = params.coordSys;
          const startCoord = api.coord([api.value(1), categoryIndex]);
          const endCoord = api.coord([api.value(2), categoryIndex]);
          const barHeight = Math.min(40, api.size([0, 1])[1] * 0.6); const minX = coordSys.x;
          const maxX = coordSys.x + coordSys.width;
          const boundedStart = Math.max(minX, startCoord[0]);
          const boundedEnd = Math.min(maxX, endCoord[0]);
          if (boundedEnd <= boundedStart) {
            return null;
          }
          return {
            type: 'rect',
            shape: {
              x: boundedStart,
              y: startCoord[1] - barHeight / 2,
              width: Math.max(1, boundedEnd - boundedStart),
              height: barHeight
            },
            style: api.style(),
            clip: true
          };
        },
        encode: { x: [1, 2], y: 0 },
        data: segments.map(segment => ({
          value: [
            resourceNames.indexOf(segment.resourceName),
            segment.start.getTime(),
            segment.end.getTime()
          ],
          resourceName: segment.resourceName,
          eventName: segment.eventName,
          typeName: segment.typeName,
          statusId: segment.statusId,
          start: segment.start,
          end: segment.end,
          durationMs: segment.durationMs,
          itemStyle: { color: segment.color }
        }))
      }];

      this.ganttChart = ganttOption;
      this.stopsKey++;
      this.$nextTick(() => this.registerGanttZoomHandler());
    },

    splitEventsByDay(events = []) {
      const segments = [];
      events.forEach((event) => {
        const resourceName = event?.resourceName || 'Sem recurso';
        const eventName = event?.eventName || 'Sem nome';
        const typeName = event?.typeName || 'Sem tipo';
        const statusId = event?.statusId;
        const color = event?.color || GANTT_BAR_COLOR;
        const startDate = this.parseDate(event?.startTime);
        const endDate = this.parseDate(event?.endTime);
        if (!startDate || !endDate || endDate <= startDate) return;

        let segmentStart = new Date(startDate);
        while (segmentStart < endDate) {
          const dayStart = this.getStartOfDay(segmentStart);
          const nextDayStart = new Date(dayStart.getTime() + DAY_IN_MS);
          const segmentEnd = endDate > nextDayStart ? nextDayStart : endDate;
          const durationMs = segmentEnd.getTime() - segmentStart.getTime();
          if (durationMs <= 0) break;

          segments.push({
            resourceName,
            eventName,
            typeName,
            statusId,
            color,
            start: new Date(segmentStart),
            end: new Date(segmentEnd),
            durationMs
          });

          segmentStart = new Date(segmentEnd);
        }
      });

      segments.sort((a, b) => {
        const byResource = a.resourceName.localeCompare(b.resourceName, 'pt-BR', { sensitivity: 'base' });
        if (byResource !== 0) return byResource;
        return a.start.getTime() - b.start.getTime();
      });

      return segments;
    },

    parseDate(value) {
      if (!value) return null;
      if (value instanceof Date) {
        return new Date(value.getTime());
      }
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? null : date;
    },

    getStartOfDay(date) {
      const base = date instanceof Date ? new Date(date.getTime()) : this.parseDate(date);
      if (!base) return null;
      base.setHours(0, 0, 0, 0);
      return base;
    },

    formatDateTime(value) {
      const date = value instanceof Date ? value : this.parseDate(value);
      if (!date) return '-';
      return `${padNumber(date.getDate())}/${padNumber(date.getMonth() + 1)}/${date.getFullYear()} ${padNumber(date.getHours())}:${padNumber(date.getMinutes())}`;
    },

    formatDuration(durationMs) {
      if (!durationMs || durationMs <= 0) return '0min';
      const totalMinutes = Math.max(1, Math.round(durationMs / 60000));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours && minutes) return `${hours}h ${minutes}min`;
      if (hours) return `${hours}h`;
      return `${minutes}min`;
    },

    registerGanttZoomHandler() {
      const chart = this.getGanttChartInstance();
      if (!chart) return;
      if (this.ganttZoomHandler) {
        chart.off('dataZoom', this.ganttZoomHandler);
        this.ganttZoomHandler = null;
      }
      const handler = (event) => this.handleGanttZoomEvent(event);
      chart.on('dataZoom', handler);
      this.ganttZoomHandler = handler;
    },

    detachGanttZoomHandler() {
      const chart = this.getGanttChartInstance();
      if (chart && this.ganttZoomHandler) {
        chart.off('dataZoom', this.ganttZoomHandler);
      }
      this.ganttZoomHandler = null;
    },

    getGanttChartInstance() {
      const component = this.$refs.ganttChartComponent;
      if (!component || !component.$refs || !component.$refs.line) return null;
      return component.$refs.line.chart || null;
    },

    handleGanttZoomEvent(event) {
      const range = this.resolveGanttZoomRange(event);
      if (!range) return;
      this.updateGanttAxisModeByRange(range.start, range.end);
    },

    resolveGanttZoomRange(event) {
      const payload = (event && Array.isArray(event.batch) && event.batch.length) ? event.batch[0] : (event || {});
      if (!payload) return null;
      const { min, max } = this.ganttAxisLimits || {};
      if (min == null || max == null) return null;

      const result = { start: null, end: null };
      if (payload.startValue != null) {
        result.start = new Date(payload.startValue).getTime();
      } else if (typeof payload.start === 'number') {
        result.start = min + (max - min) * (payload.start / 100);
      }

      if (payload.endValue != null) {
        result.end = new Date(payload.endValue).getTime();
      } else if (typeof payload.end === 'number') {
        result.end = min + (max - min) * (payload.end / 100);
      }

      if (result.start == null || result.end == null) return null;
      const start = Math.min(result.start, result.end);
      const end = Math.max(result.start, result.end);
      return { start, end };
    },

    updateGanttAxisModeByRange(start, end) {
      if (!this.ganttAxisLimits || this.ganttAxisLimits.min == null || this.ganttAxisLimits.max == null) return;
      const clampedStart = Math.max(this.ganttAxisLimits.min, Math.min(start, this.ganttAxisLimits.max));
      const clampedEnd = Math.max(this.ganttAxisLimits.min, Math.min(end, this.ganttAxisLimits.max));
      this.ganttCurrentRange = { start: clampedStart, end: clampedEnd };
      const range = Math.abs(clampedEnd - clampedStart);
      const axisDisplay = this.resolveGanttAxisDisplay(range);
      if (
        axisDisplay.mode === this.ganttAxisMode.mode &&
        axisDisplay.interval === this.ganttAxisMode.interval
      ) {
        return;
      }
      this.ganttAxisMode = axisDisplay;
      const updatedDataZoom = Array.isArray(this.ganttChart.dataZoom)
        ? this.ganttChart.dataZoom.map(item => ({
          ...item,
          startValue: this.ganttCurrentRange.start,
          endValue: this.ganttCurrentRange.end
        }))
        : this.ganttChart.dataZoom;
      this.ganttChart = {
        ...this.ganttChart,
        xAxis: buildTimeAxisConfig({
          axisMin: this.ganttAxisLimits.min,
          axisMax: this.ganttAxisLimits.max,
          mode: axisDisplay.mode,
          interval: axisDisplay.interval
        }),
        dataZoom: updatedDataZoom
      };
    },

    resolveGanttAxisDisplay(range) {
      if (range <= TWELVE_HOURS_IN_MS) {
        if (range <= 4 * HOUR_IN_MS) {
          return { mode: 'hour', interval: QUARTER_HOUR_IN_MS };
        }
        if (range <= 8 * HOUR_IN_MS) {
          return { mode: 'hour', interval: HALF_HOUR_IN_MS };
        }
        return { mode: 'hour', interval: HOUR_IN_MS };
      }
      return { mode: 'day', interval: DAY_IN_MS };
    },

    convertChartValues(valuesInMinutes) {
      const maxValue = Math.max(...valuesInMinutes.map(v => Number(v)));
      const shouldConvertToHours = maxValue >= 60;

      if (shouldConvertToHours) {
        return {
          values: valuesInMinutes.map(v => Number(v) / 60),
          originalValues: valuesInMinutes,
          unit: 'h',
          isConverted: true
        };
      }

      return {
        values: valuesInMinutes,
        originalValues: valuesInMinutes,
        unit: 'min',
        isConverted: false
      };
    },

    formatTooltipTime(totalMinutes) {
      const minutes = Number(totalMinutes);
      if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}min`;
      }
      return `${Math.round(minutes)}min`;
    },

    createTooltipFormatter(originalValues, isConverted) {
      return (params) => {
        // params é um array quando trigger é 'axis'
        if (Array.isArray(params)) {
          params = params[0];
        }
        const index = params.dataIndex;
        const originalValue = originalValues[index];
        const timeFormatted = this.formatTooltipTime(originalValue);
        return `${params.name}<br/>${params.marker}${params.seriesName}: ${timeFormatted}`;
      };
    },

    createAxisLabelFormatter(isConverted) {
      return (value) => {
        if (isConverted) {
          // Mostra apenas números inteiros das horas
          return Math.round(value).toString();
        }
        return Math.round(value).toString();
      };
    },

    updateAreaChart(stopEventsByArea = []) {
      if (!Array.isArray(stopEventsByArea) || stopEventsByArea.length === 0) {
        // Limpa o gráfico quando não houver dados
        this.stopsByArea.xAxis.data = [];
        this.stopsByArea.series[0].data = [];
        this.stopsByArea.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByArea.tooltip = undefined;
        return;
      }

      const areaLabels = stopEventsByArea.map(item => item?.areaName || 'Sem nome');
      const areaValuesMinutes = stopEventsByArea.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(areaValuesMinutes);

      const coloredData = areaLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.areaColors, 'Area') }
      }));

      this.stopsByArea.xAxis.data = areaLabels;
      this.stopsByArea.series[0].data = coloredData;
      this.stopsByArea.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      this.stopsByArea.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
    },

    updateShiftChart(stopEventsByShift = []) {
      if (!Array.isArray(stopEventsByShift) || stopEventsByShift.length === 0) {
        this.stopsByShift.xAxis.data = [];
        this.stopsByShift.series[0].data = [];
        this.stopsByShift.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByShift.tooltip = undefined;
        return;
      }

      const shiftLabels = stopEventsByShift.map(item => item?.shiftName || 'Sem nome');
      const shiftValuesMinutes = stopEventsByShift.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(shiftValuesMinutes);

      const coloredData = shiftLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.shiftColors, 'Shift') }
      }));

      this.stopsByShift.xAxis.data = shiftLabels;
      this.stopsByShift.series[0].data = coloredData;
      this.stopsByShift.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      this.stopsByShift.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
      // this.filtersHelper.updateFilterOptions(this.filters, 'shift', shiftLabels, true);
    },

    updateMonthChart(stopEventsByMonth = []) {
      if (!Array.isArray(stopEventsByMonth) || stopEventsByMonth.length === 0) {
        this.stopsByMonth.xAxis.data = [];
        this.stopsByMonth.series[0].data = [];
        this.stopsByMonth.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByMonth.tooltip = undefined;
        return;
      }

      const monthLabels = stopEventsByMonth.map(item => item?.label || 'Sem nome');
      const monthValuesMinutes = stopEventsByMonth.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(monthValuesMinutes);

      const coloredData = monthLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.monthColors, 'Month') }
      }));

      this.stopsByMonth.xAxis.data = monthLabels;
      this.stopsByMonth.series[0].data = coloredData;
      this.stopsByMonth.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      this.stopsByMonth.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
    },

    updateDayChart(stopEventsByDay = []) {
      if (!Array.isArray(stopEventsByDay) || stopEventsByDay.length === 0) {
        this.stopsByDay.xAxis.data = [];
        this.stopsByDay.series[0].data = [];
        this.stopsByDay.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByDay.tooltip = undefined;
        return;
      }

      const dayLabels = stopEventsByDay.map(item => item?.label || 'Sem nome');
      const dayValuesMinutes = stopEventsByDay.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(dayValuesMinutes);

      const coloredData = dayLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.dayColors, 'Day') }
      }));

      this.stopsByDay.xAxis.data = dayLabels;
      this.stopsByDay.series[0].data = coloredData;
      this.stopsByDay.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      this.stopsByDay.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
    },

    updateResourceChart(stopEventsByResource = []) {
      if (!Array.isArray(stopEventsByResource) || stopEventsByResource.length === 0) {
        this.stopsByResource.xAxis.data = [];
        this.stopsByResource.series[0].data = [];
        this.stopsByResource.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByResource.tooltip = undefined;
        return;
      }

      // Construir resourceMap (nome -> ID) para enviar na próxima requisição
      stopEventsByResource.forEach(item => {
        if (item?.resourceName && item?.resourceId) {
          this.resourceMap[item.resourceName] = item.resourceId;
        }
      });

      const resourceLabels = stopEventsByResource.map(item => item?.resourceName || 'Sem nome');
      const resourceValuesMinutes = stopEventsByResource.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(resourceValuesMinutes);

      const coloredData = resourceLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.resourceColors, 'Resource') }
      }));

      this.stopsByResource.xAxis.data = resourceLabels;
      this.stopsByResource.series[0].data = coloredData;
      this.stopsByResource.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      // this.filtersHelper.updateFilterOptions(this.filters, 'resource', resourceLabels, true);
      this.stopsByResource.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
    },

    updateTypeChart(stopEventsByType = []) {
      if (!Array.isArray(stopEventsByType) || stopEventsByType.length === 0) {
        this.stopsByType.xAxis.data = [];
        this.stopsByType.series[0].data = [];
        this.stopsByType.yAxis.axisLabel = { formatter: () => '' };
        this.stopsByType.tooltip = undefined;
        return;
      }

      const typeLabels = stopEventsByType.map(item => item?.typeName || 'Sem nome');
      const typeValuesMinutes = stopEventsByType.map(item => Number(item?.totalDurationMinutes || 0));

      const converted = this.convertChartValues(typeValuesMinutes);

      const coloredData = typeLabels.map((label, index) => ({
        value: converted.values[index] ?? 0,
        itemStyle: { color: this.getColorForCategory(label, this.typeColors, 'Type') }
      }));

      this.stopsByType.xAxis.data = typeLabels;
      this.stopsByType.series[0].data = coloredData;
      this.stopsByType.yAxis.axisLabel = {
        formatter: this.createAxisLabelFormatter(converted.isConverted)
      };
      this.stopsByType.tooltip = {
        trigger: 'axis',
        formatter: this.createTooltipFormatter(converted.originalValues, converted.isConverted)
      };
    },

    updateReasonsChart(stopReasons = []) {
      if (!Array.isArray(stopReasons) || stopReasons.length === 0) {
        this.stopsEvents.xAxis.data = [];
        this.stopsEvents.series[0].data = [];
        this.stopsEvents.series[1].data = [];
        return;
      }

      // Criar array de objetos para facilitar a ordenação (incluindo cor)
      const reasonsData = stopReasons.map(item => ({
        label: item?.statusName || 'Sem nome',
        value: Number(item?.totalDurationMinutes || 0),
        color: item?.color || '#5A8DEE'
      }));

      // Ordenar do maior para o menor
      reasonsData.sort((a, b) => b.value - a.value);

      // Extrair labels, valores e cores ordenados
      const reasonLabels = reasonsData.map(item => item.label);
      const reasonValuesMinutes = reasonsData.map(item => item.value);
      const reasonColors = reasonsData.map(item => item.color);

      // Calcular o total para o percentual
      const total = reasonValuesMinutes.reduce((sum, val) => sum + val, 0);

      // Calcular percentuais acumulados
      let accumulated = 0;
      const accumulatedPercentages = reasonValuesMinutes.map(value => {
        accumulated += value;
        return total > 0 ? (accumulated / total) * 100 : 0;
      });

      // Preparar dados das barras com cores individuais
      const barData = reasonValuesMinutes.map((value, index) => ({
        value: value,
        itemStyle: { color: reasonColors[index] }
      }));

      // Atualizar o gráfico
      this.stopsEvents.xAxis.data = reasonLabels;
      this.stopsEvents.series[0].data = barData;
      this.stopsEvents.series[1].data = accumulatedPercentages;

      // Tooltip customizado para Pareto
      this.stopsEvents.tooltip = {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params) => {
          if (!Array.isArray(params) || params.length === 0) return '';

          const index = params[0].dataIndex;
          const categoryName = reasonLabels[index];
          const timeValue = reasonValuesMinutes[index];
          const accumulatedValue = accumulatedPercentages[index];
          const barColor = reasonColors[index];

          const timeFormatted = this.formatTooltipTime(timeValue);
          const percentOfTotal = total > 0 ? ((timeValue / total) * 100).toFixed(1) : '0.0';

          return `
            <div style="padding: 4px 0;">
              <strong>${categoryName}</strong><br/>
              <span style="color: ${barColor};">●</span> Tempo parado: <strong>${timeFormatted}</strong> (${percentOfTotal}%)<br/>
              <span style="color: #FF4560;">●</span> % acumulado: <strong>${accumulatedValue.toFixed(1)}%</strong>
            </div>
          `;
        }
      };
    },

    updateTable(details = []) {
      if (!Array.isArray(details) || details.length === 0) {
        console.warn('Nenhum dado de tabela de paradas disponível');
        this.tableItems = [];
        return;
      }

      this.tableItems = details.map(item => ({
        ordem: item?.orderNumber || 0,
        recurso: item?.resourceName || '-',
        evento: item?.eventName || '-',
        tempo: item?.duration || '-',
        inicio: item?.startTime ? this.formatDateTime(item.startTime) : '-',
        fim: item?.endTime ? this.formatDateTime(item.endTime) : '-'
      }));
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      } catch (error) {
        return dateString;
      }
    },

    safeJSONParse(value) {
      try {
        return value ? JSON.parse(value) : null;
      } catch (error) {
        return null;
      }
    },

    exportStopsCsv() {
      const headers = this.tableFields.map(f => f.label);
      const keys = this.tableFields.map(f => f.key);

      const rows = this.tableItems.map(item => keys.map(k => item[k]));
      const csv = this.buildCsv([headers, ...rows], ';');

      const now = new Date().toISOString().split('T')[0];

      this.downloadCsv(csv, `paradas_${now}.csv`);
    },
    buildCsv(matrix, sep = ',') {
      return matrix
        .map(row =>
          row
            .map(v => {
              const val = v == null ? '' : String(v);
              const needsQuote = /[",\n;]/.test(val) || val.includes(sep);
              const escaped = val.replace(/"/g, '""');
              return needsQuote ? `"${escaped}"` : escaped;
            })
            .join(sep)
        )
        .join('\n');
    },
    downloadCsv(csvContent, filename) {
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },

    updateCharts() {

      //  Atualizar gráfico por recurso
      const resourceCounts = {};
      filtered.forEach(item => {
        resourceCounts[item.recurso] = (resourceCounts[item.recurso] || 0) + 1;
      });

      this.stopsByResource.xAxis.data = Object.keys(resourceCounts);
      this.stopsByResource.series[0].data = Object.values(resourceCounts);

      // Faça o mesmo para outros gráficos...
    },
    applyFilters() {
      this.filtersOpen = false;
      this.loadStopsData();
    },
    clearFilters() {
      this.filtersHelper.clearFilters(this.filters);
      this.initializeDates();
      this.loadStopsData();
    },

    initializeDates() {
      // Inicializar com os últimos 30 dias
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);

      this.startDate = this.formatDateToISO(startDate);
      this.endDate = this.formatDateToISO(endDate);
    },

    formatDateToISO(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    updateDateRangeText() {
      if (!this.startDate || !this.endDate) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 7);

        this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
      } else {
        const startDate = this.parseLocalDate(this.startDate);
        const endDate = this.parseLocalDate(this.endDate);

        this.dateRangeText = `${this.formatDateToBrazilian(startDate)} a ${this.formatDateToBrazilian(endDate)}`;
      }
    },

    parseLocalDate(dateStr) {
      if (!dateStr) return null;
      if (dateStr instanceof Date) return new Date(dateStr.getTime());
      const str = String(dateStr);
      if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d);
      }
      return new Date(dateStr);
    },

    formatDateToBrazilian(date) {
      if (!date) return '';

      const d = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)
        ? this.parseLocalDate(date)
        : new Date(date);
      if (!d || Number.isNaN(d.getTime())) return '';

      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();

      return `${day}-${month}-${year}`;
    },

    setQuickFilter(days) {
      this.filterDays = days;

      // Atualizar as datas baseado no filtro rápido
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      this.startDate = this.formatDateToISO(startDate);
      this.endDate = this.formatDateToISO(endDate);
    },

    onManualDateChange() {
      // Quando o usuário alterar manualmente as datas, limpar o filterDays
      this.filterDays = null;
    }
  }
};
</script>

<style lang="scss" scoped>
#stops-page {
  .header-block {
    padding: 10px 14px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    font-size: 18px;
    font-weight: 700;
    color: #4c4541;
  }

  .date-range-info {
    font-size: 12px;
    color: #6c757d;
    margin-left: 12px;
    font-weight: 500;
  }

  .error-info {
    font-size: 12px;
    color: #ea5455;
    margin-left: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
  }

  .body-block {
    padding: 4px 14px 20px 14px;
  }

  .filters-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 12px;

    .filter+.filter {
      margin-top: 8px;
    }

    .filter-label {
      display: block;
      font-weight: 600;
      font-size: 12px;
      margin-bottom: 6px;
    }

    .date-input-sidebar {
      width: 100%;
      padding: 6px 8px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      font-size: 14px;
      color: #6c757d;
      background-color: #fff;
      cursor: pointer;
      transition: border-color 0.2s;

      &:hover {
        border-color: #f3811a;
      }

      &:focus {
        outline: none;
        border-color: #f3811a;
        box-shadow: 0 0 0 2px rgba(243, 129, 26, 0.1);
      }
    }

    .quick-filter-buttons {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 6px;
    }

    .quick-filter-btn {
      padding: 6px 10px;
      border: 1px solid #d8d6de;
      border-radius: 4px;
      background-color: #fff;
      color: #6c757d;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        border-color: #9f5714;
        color: #9f5714;
      }

      &.active {
        background-color: #9f5714;
        border-color: #9f5714;
        color: #fff;
      }
    }
  }

  .chart-card,
  .table-card {
    padding: 16px 16px 0px 16px;
    display: flex;
    flex-direction: column;
    min-height: 320px;
  }

  .chart-header {
    font-weight: 700;
    margin-bottom: 8px;
  }

  .chart-description {
    font-size: 12px;
    color: #6c757d;
    margin-bottom: 8px;
  }

  .chart-body {
    flex: 1 1 auto;
  }

  .table-wrapper {
    flex: 1 1 auto;
    margin-top: 8px;
    overflow-x: auto;
  }

  .table-card .d-flex {
    margin-top: 12px;
  }

  .stops-table {
    font-size: 12px;
  }

  .btn-filters {
    border-color: #9f5714 !important;
    color: #9f5714 !important;

    &:hover {
      background-color: #9f5714 !important;
      color: #fff !important;
    }
  }

  ::v-deep .b-sidebar-footer {
    .btn-primary {
      background-color: #9f5714 !important;
      border-color: #9f5714 !important;

      &:hover {
        background-color: #7d4510 !important;
        border-color: #7d4510 !important;
      }
    }
  }
}

@media (max-width: 1199.98px) {
  #stops-page {

    .chart-card,
    .table-card {
      min-height: 280px;
    }
  }
}

@media (max-width: 767.98px) {
  #stops-page {
    .body-block {
      padding: 4px 8px 12px 8px;
    }

    .chart-card,
    .table-card {
      min-height: 260px;
      padding: 16px 16px 0px 16px;
    }

    .table-wrapper {
      margin-top: 6px;
    }

    .chart-header {
      font-size: 12px;
      margin-bottom: 6px;
    }
  }
}
</style>
